<template>
  <ComponentHeader title="Entity Configuration" subtitle="Define entity types, manage their attributes, and configure display components">
      <button
        @click="showCreateModal = true"
        class="btn-primary"
      >
        <font-awesome-icon :icon="['fas', 'plus']" />
        Create New Entity Type
      </button>
    </ComponentHeader>

  <div class="p-4">
    <!-- Entity Types List -->
    <div class="bg-white rounded-lg shadow">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Entity Type
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Attributes
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Table Columns
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Display Components
            </th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="entityType in entityTypes" :key="entityType.id">
            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
              {{ entityType.type_name }}
            </td>
            <td class="px-6 py-4 text-sm text-gray-500">
              {{ getAttributeCount(entityType.id) }} attributes
            </td>
            <td class="px-6 py-4 text-sm text-gray-500">
              {{ getTableColumnCount(entityType.display_config) }} columns
            </td>
            <td class="px-6 py-4 text-sm text-gray-500">
              {{ getComponentCount(entityType.display_config) }} components
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <span
                :class="entityType.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'"
                class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
              >
                {{ entityType.is_active ? 'Active' : 'Inactive' }}
              </span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
              <button
                @click="manageAttributes(entityType)"
                class="text-primary-600 hover:text-primary-900 mr-3"
              >
                Manage Attributes
              </button>
              <button
                @click="configureDisplay(entityType)"
                class="text-purple-600 hover:text-purple-900 mr-3"
              >
                Configure Display
              </button>
              <button
                v-if="hasTemplate(entityType.type_name) && getAttributeCount(entityType.id) === 0"
                @click="initializeFromTemplate(entityType)"
                class="text-green-600 hover:text-green-900"
              >
                Initialize Template
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Manage Attributes Modal (existing - unchanged) -->
    <div
      v-if="showAttributesModal"
      class="fixed inset-0 bg-gray-500/75 overflow-y-auto h-full w-full z-50"
      @click.self="closeAttributesModal"
    >
      <div class="relative top-10 mx-auto p-5 w-full max-w-4xl">
        <div class="bg-white rounded-lg shadow-xl max-h-[80vh] overflow-hidden flex flex-col">
          <!-- Modal Header -->
          <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 class="text-lg font-semibold text-gray-900">
              Manage Attributes - {{ editingEntityType?.type_name }}
            </h3>
            <button
              @click="closeAttributesModal"
              class="text-gray-400 hover:text-gray-500"
            >
              <font-awesome-icon :icon="['fas', 'times']" class="h-6 w-6" />
            </button>
          </div>

          <!-- Modal Body -->
          <div class="flex-1 overflow-y-auto p-6">
            <!-- Add Attribute Button -->
            <div class="mb-4 flex justify-between items-center">
              <h4 class="text-sm font-medium text-gray-700">Entity Attributes</h4>
              <button
                @click="showAddAttributeForm = true"
                class="btn-primary-sm"
              >
                <font-awesome-icon :icon="['fas', 'plus']" />
                Add Attribute
              </button>
            </div>

            <!-- Add/Edit Attribute Form -->
            <div v-if="showAddAttributeForm" class="mb-4 p-4 bg-gray-50 rounded-lg">
              <h5 class="text-sm font-semibold text-gray-700 mb-3">
                {{ editingAttribute ? 'Edit Attribute' : 'New Attribute' }}
              </h5>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Field Name</label>
                  <input
                    v-model="newAttribute.name"
                    placeholder="e.g., phone_number"
                    :disabled="!!editingAttribute"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                  <input
                    v-model="newAttribute.display_name"
                    placeholder="e.g., Phone Number"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Field Type</label>
                  <select
                    v-model="newAttribute.field_type"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
                  >
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                    <option value="date">Date</option>
                    <option value="boolean">Boolean</option>
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="url">URL</option>
                    <option value="json">JSON</option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Display Group</label>
                  <input
                    v-model="newAttribute.display_group"
                    placeholder="e.g., Contact Info"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
                  />
                </div>
                <div class="col-span-2">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    v-model="newAttribute.description"
                    placeholder="Brief description of this field"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
                    rows="2"
                  />
                </div>
                <div class="col-span-2 flex items-center gap-4">
                  <label class="flex items-center">
                    <input
                      v-model="newAttribute.is_required"
                      type="checkbox"
                      class="rounded border-gray-300 text-blue-600 focus:ring-blue-dark mr-2"
                    />
                    <span class="text-sm text-gray-700">Required Field</span>
                  </label>
                </div>
              </div>
              <div class="mt-4 flex justify-end gap-2">
                <button
                  @click="cancelAddAttribute"
                  class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  @click="saveAttribute"
                  class="btn-primary-sm"
                >
                  <font-awesome-icon :icon="['fas', 'save']" />
                  {{ editingAttribute ? 'Update' : 'Save' }} Attribute
                </button>
              </div>
            </div>

            <!-- Attributes List -->
            <div class="space-y-2">
              <div
                v-for="(attribute, index) in entityAttributes"
                :key="attribute.id"
                class="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div class="flex-1">
                  <div class="flex items-center gap-3">
                    <span class="font-medium text-sm text-gray-900">{{ attribute.display_name }}</span>
                    <span class="text-xs text-gray-500 font-mono bg-gray-200 px-2 py-0.5 rounded">{{ attribute.name }}</span>
                    <span class="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
                      {{ attribute.field_type }}
                    </span>
                    <span v-if="attribute.is_required" class="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">
                      Required
                    </span>
                  </div>
                  <div v-if="attribute.description" class="text-xs text-gray-500 mt-1">
                    {{ attribute.description }}
                  </div>
                </div>
                <div class="flex items-center gap-1">
                  <button
                    @click="moveAttribute(index, -1)"
                    :disabled="index === 0"
                    class="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                    title="Move up"
                  >
                    <font-awesome-icon :icon="['fas', 'arrow-up']" class="text-xs" />
                  </button>
                  <button
                    @click="moveAttribute(index, 1)"
                    :disabled="index === entityAttributes.length - 1"
                    class="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                    title="Move down"
                  >
                    <font-awesome-icon :icon="['fas', 'arrow-down']" class="text-xs" />
                  </button>
                  <button
                    @click="editAttribute(attribute)"
                    class="p-1.5 text-blue-600 hover:text-blue-800"
                    title="Edit"
                  >
                    <font-awesome-icon :icon="['fas', 'edit']" />
                  </button>
                  <button
                    @click="deleteAttribute(attribute)"
                    class="p-1.5 text-red-600 hover:text-red-800"
                    title="Delete"
                  >
                    <font-awesome-icon :icon="['fas', 'trash']" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Display Configuration Modal - ENHANCED WITH TABLE CONFIG -->
    <div
      v-if="showDisplayModal"
      class="fixed inset-0 bg-gray-500/75 overflow-y-auto h-full w-full z-50"
      @click.self="closeDisplayModal"
    >
      <div class="relative top-10 mx-auto p-5 w-full max-w-6xl">
        <div class="bg-white rounded-lg shadow-xl max-h-[85vh] overflow-hidden flex flex-col">
          <!-- Modal Header -->
          <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 class="text-lg font-semibold text-gray-900">
              Configure Display - {{ editingEntityType?.type_name }}
            </h3>
            <button
              @click="closeDisplayModal"
              class="text-gray-400 hover:text-gray-500"
            >
              <font-awesome-icon :icon="['fas', 'times']" class="h-6 w-6" />
            </button>
          </div>

          <!-- Tabs for different configuration sections -->
          <div class="border-b border-gray-200">
            <nav class="flex -mb-px">
              <button
                @click="activeDisplayTab = 'table'"
                :class="[
                  'px-6 py-3 text-sm font-medium border-b-2 transition-colors',
                  activeDisplayTab === 'table'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                ]"
              >
                Table Columns
              </button>
              <button
                @click="activeDisplayTab = 'pages'"
                :class="[
                  'px-6 py-3 text-sm font-medium border-b-2 transition-colors',
                  activeDisplayTab === 'pages'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                ]"
              >
                Page Layout
              </button>
            </nav>
          </div>

          <!-- Modal Body -->
          <div class="flex-1 overflow-y-auto p-6">
            <!-- TABLE COLUMNS CONFIGURATION -->
            <div v-if="activeDisplayTab === 'table'">
              <div class="mb-4">
                <h4 class="text-sm font-medium text-gray-700 mb-2">Configure Table Columns</h4>
                <p class="text-xs text-gray-500">Define which columns appear in Entity_Detail_Table for this entity type.</p>
              </div>

              <div class="grid grid-cols-2 gap-6">
                <!-- Available Attributes (Left Panel) -->
                <div class="border rounded-lg p-4 bg-gray-50">
                  <h5 class="text-sm font-semibold text-gray-700 mb-3">Available Attributes</h5>
                  <div class="space-y-2 max-h-96 overflow-y-auto">
                    <div
                      v-for="attr in availableAttributesForTable"
                      :key="'avail-' + attr.name"
                      @click="addColumnFromAttribute(attr)"
                      class="p-2 bg-white border rounded cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-colors"
                    >
                      <div class="flex items-center justify-between">
                        <div>
                          <div class="text-sm font-medium text-gray-900">{{ attr.display_name }}</div>
                          <div class="text-xs text-gray-500 font-mono">{{ attr.name }}</div>
                        </div>
                        <font-awesome-icon :icon="['fas', 'plus']" class="text-blue-600 text-xs" />
                      </div>
                    </div>
                  </div>

                  <div class="mt-4 pt-4 border-t">
                    <h5 class="text-sm font-semibold text-gray-700 mb-2">Special Columns</h5>
                    <button
                      @click="addSpecialColumn('identifier')"
                      class="w-full mb-2 p-2 bg-white border rounded text-left hover:bg-blue-50 hover:border-blue-300 transition-colors"
                    >
                      <div class="text-sm font-medium text-gray-900">+ Identifier Column</div>
                      <div class="text-xs text-gray-500">NPI, TIN, MRN, etc.</div>
                    </button>
                    <button
                      @click="addSpecialColumn('action')"
                      class="w-full p-2 bg-white border rounded text-left hover:bg-blue-50 hover:border-blue-300 transition-colors"
                    >
                      <div class="text-sm font-medium text-gray-900">+ Action Column</div>
                      <div class="text-xs text-gray-500">Sync, Delete, etc.</div>
                    </button>
                  </div>
                </div>

                <!-- Selected Columns (Right Panel) -->
                <div class="border rounded-lg p-4">
                  <div class="flex justify-between items-center mb-3">
                    <h5 class="text-sm font-semibold text-gray-700">Table Columns (in order)</h5>
                    <button
                      v-if="displayConfig.table_config?.columns?.length > 0"
                      @click="clearAllTableColumns"
                      class="text-xs text-red-600 hover:text-red-800"
                    >
                      Clear All
                    </button>
                  </div>

                  <div v-if="!displayConfig.table_config?.columns?.length" class="text-sm text-gray-500 italic text-center py-8">
                    No columns configured. Add attributes from the left panel.
                  </div>

                  <div v-else class="space-y-2 max-h-96 overflow-y-auto">
                    <div
                      v-for="(column, index) in displayConfig.table_config.columns"
                      :key="'col-' + index"
                      class="p-3 bg-gray-50 border rounded"
                    >
                      <div class="flex items-start justify-between mb-2">
                        <div class="flex-1">
                          <div class="text-sm font-medium text-gray-900">
                            {{ column.header_name || getColumnDisplayName(column) }}
                          </div>
                          <div class="text-xs text-gray-500">
                            {{ getColumnTypeLabel(column) }}
                          </div>
                        </div>
                        <div class="flex items-center gap-1">
                          <button
                            @click="moveTableColumn(index, -1)"
                            :disabled="index === 0"
                            class="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                            title="Move up"
                          >
                            <font-awesome-icon :icon="['fas', 'arrow-up']" class="text-xs" />
                          </button>
                          <button
                            @click="moveTableColumn(index, 1)"
                            :disabled="index === displayConfig.table_config.columns.length - 1"
                            class="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                            title="Move down"
                          >
                            <font-awesome-icon :icon="['fas', 'arrow-down']" class="text-xs" />
                          </button>
                          <button
                            @click="editTableColumn(index)"
                            class="p-1 text-blue-600 hover:text-blue-800"
                            title="Edit"
                          >
                            <font-awesome-icon :icon="['fas', 'edit']" class="text-xs" />
                          </button>
                          <button
                            @click="removeTableColumn(index)"
                            class="p-1 text-red-600 hover:text-red-800"
                            title="Remove"
                          >
                            <font-awesome-icon :icon="['fas', 'times']" class="text-xs" />
                          </button>
                        </div>
                      </div>

                      <!-- Column configuration -->
                      <div v-if="editingColumnIndex === index" class="mt-2 pt-2 border-t space-y-2">
                        <div>
                          <label class="block text-xs text-gray-600 mb-1">Header Name</label>
                          <input
                            v-model="column.header_name"
                            placeholder="Optional override"
                            class="w-full px-2 py-1 text-xs border rounded"
                          />
                        </div>
                        <div class="grid grid-cols-2 gap-2">
                          <div>
                            <label class="block text-xs text-gray-600 mb-1">Width (px)</label>
                            <input
                              v-model.number="column.width"
                              type="number"
                              placeholder="150"
                              class="w-full px-2 py-1 text-xs border rounded"
                            />
                          </div>
                          <div v-if="column.type === 'identifier'">
                            <label class="block text-xs text-gray-600 mb-1">Identifier Type</label>
                            <select
                              v-model="column.identifier_type"
                              class="w-full px-2 py-1 text-xs border rounded"
                            >
                              <option value="NPI">NPI</option>
                              <option value="TIN">TIN</option>
                              <option value="MRN">MRN</option>
                              <option value="BENE_MBI_ID">Medicare MBI</option>
                            </select>
                          </div>
                          <div v-if="column.type === 'action'">
                            <label class="block text-xs text-gray-600 mb-1">Action Type</label>
                            <select
                              v-model="column.action_type"
                              class="w-full px-2 py-1 text-xs border rounded"
                            >
                              <option value="sync">Sync</option>
                              <option value="delete">Delete</option>
                            </select>
                          </div>
                        </div>
                        <div class="flex items-center gap-3 text-xs">
                          <label class="flex items-center">
                            <input
                              v-model="column.sortable"
                              type="checkbox"
                              class="rounded border-gray-300 text-blue-600 focus:ring-blue-dark mr-1"
                            />
                            <span>Sortable</span>
                          </label>
                          <label class="flex items-center">
                            <input
                              v-model="column.filterable"
                              type="checkbox"
                              class="rounded border-gray-300 text-blue-600 focus:ring-blue-dark mr-1"
                            />
                            <span>Filterable</span>
                          </label>
                        </div>
                        <button
                          @click="editingColumnIndex = null"
                          class="text-xs text-blue-600 hover:text-blue-800"
                        >
                          Done Editing
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- PAGE LAYOUT CONFIGURATION (existing) -->
            <div v-if="activeDisplayTab === 'pages'">
              <div class="mb-6">
                <div class="flex justify-between items-center mb-4">
                  <h4 class="text-sm font-medium text-gray-700">Page Tabs</h4>
                  <button
                    @click="addTab"
                    class="btn-primary-sm"
                  >
                    <font-awesome-icon :icon="['fas', 'plus']" />
                    Add Tab
                  </button>
                </div>

                <!-- Tabs List -->
                <div class="space-y-4">
                  <div
                    v-for="(tabName, tabIndex) in orderedTabNames"
                    :key="tabName"
                    class="border rounded-lg p-4"
                  >
                    <div class="flex justify-between items-center mb-3">
                      <input
                        :value="tabName"
                        @input="renameTab(tabName, $event.target.value)"
                        class="font-medium text-gray-900 bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none"
                      />
                      <div class="flex items-center gap-1">
                        <button
                          @click="moveTab(tabIndex, -1)"
                          :disabled="tabIndex === 0"
                          class="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                          title="Move up"
                        >
                          <font-awesome-icon :icon="['fas', 'arrow-up']" />
                        </button>
                        <button
                          @click="moveTab(tabIndex, 1)"
                          :disabled="tabIndex === orderedTabNames.length - 1"
                          class="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                          title="Move down"
                        >
                          <font-awesome-icon :icon="['fas', 'arrow-down']" />
                        </button>
                        <button
                          @click="removeTab(tabName)"
                          class="p-1 text-red-600 hover:text-red-800"
                          title="Remove"
                        >
                          <font-awesome-icon :icon="['fas', 'times']" />
                        </button>
                      </div>
                    </div>

                    <!-- Components in Tab -->
                    <div class="space-y-2">
                      <div class="flex justify-between items-center">
                        <span class="text-sm text-gray-600">Components:</span>
                        <button
                          @click="addComponent(tabName)"
                          class="text-xs text-blue-600 hover:text-blue-800"
                        >
                          <font-awesome-icon :icon="['fas', 'plus']" class="mr-1" />
                          Add Component
                        </button>
                      </div>
                      <div
                        v-for="(component, index) in displayConfig.layout_config.tabs[tabName]"
                        :key="index"
                        class="flex flex-col gap-2 p-2 bg-gray-50 rounded"
                      >
                        <div class="flex items-center justify-between">
                          <select
                            v-model="component.component"
                            class="flex-1 text-sm border border-gray-300 rounded px-2 py-1"
                            @change="onComponentTypeChange(tabName, index, component)"
                          >
                            <option value="basic_info">Basic Information</option>
                            <option value="entity_context">Entity Context</option>
                            <option value="action_table">Action Items</option>
                            <option value="data_insights">Data Insights</option>
                            <option value="engagement_table">Engagements</option>
                            <option value="entity_detail_table">Entity Detail Table (Dynamic)</option>
                            <option value="staff_table">Staff (Legacy)</option>
                            <option value="threads">Threads</option>
                            <option value="surveys">Surveys</option>
                            <option value="key_metrics_detailed">Key Metrics - Detailed</option>
                            <option value="composable_key_metrics">Key Metrics (Composable)</option>
                            <option value="attribution">Contract Attribution</option>
                            <option value="attribution_health">Attribution Health</option>
                            <option value="strategic_assessments">Strategic Assessments</option>
                            <option value="adt_alerts">ADT Alerts (Admin Only)</option>
                            <option value="toc_sankey">Transitions of Care (Sankey)</option>
                            <option value="entity_table">Entity List</option>
                            <option value="quality_scorecard">Quality Patient Worklist</option>
                            <option value="quality_provider_scorecard">Quality Scorecard</option>
                            <option value="care_gap_summary">Care Gap Summary</option>
                            <option value="ppi_module">PPI</option>
                            <option value="patient_worklist">Patient Worklist</option>
                            <option value="intelligence">Pre-Meeting Brief</option>
                            <option value="engagement_tracker">Engagement Tracker</option>
                            <option value="engagement_intelligence_notice">Engagement Intelligence Notice (Banner)</option>
                            <option value="requirement_table">Notes</option>
                          </select>
                          <div class="flex items-center gap-1 ml-2">
                            <button
                              @click="moveComponent(tabName, index, -1)"
                              :disabled="index === 0"
                              class="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                              title="Move up"
                            >
                              <font-awesome-icon :icon="['fas', 'arrow-up']" class="text-xs" />
                            </button>
                            <button
                              @click="moveComponent(tabName, index, 1)"
                              :disabled="index === displayConfig.layout_config.tabs[tabName].length - 1"
                              class="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                              title="Move down"
                            >
                              <font-awesome-icon :icon="['fas', 'arrow-down']" class="text-xs" />
                            </button>
                            <button
                              @click="removeComponent(tabName, index)"
                              class="p-1 text-red-600 hover:text-red-800"
                              title="Remove"
                          >
                            <font-awesome-icon :icon="['fas', 'times']" class="text-xs" />
                          </button>
                          </div>
                        </div>

                        <!-- Entity Detail Table Configuration -->
                        <div
                          v-if="component.component === 'entity_detail_table'"
                          class="mt-2 p-3 bg-white border border-blue-200 rounded space-y-2"
                        >
                          <div class="text-xs font-semibold text-blue-700 mb-2">
                            Entity Detail Table Settings
                          </div>

                          <div class="grid grid-cols-2 gap-2">
                            <div>
                              <label class="block text-xs text-gray-600 mb-1">Custom Title</label>
                              <input
                                v-model="component.settings.title"
                                placeholder="e.g., Staff Members"
                                class="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                              />
                            </div>

                            <div>
                              <label class="block text-xs text-gray-600 mb-1">Child Entity Type</label>
                              <select
                                v-model.number="component.settings.child_entity_type_id"
                                class="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                              >
                                <option :value="null">Select entity type...</option>
                                <option
                                  v-for="type in entityTypes"
                                  :key="type.id"
                                  :value="type.id"
                                >
                                  {{ type.type_name }}
                                </option>
                              </select>
                            </div>
                          </div>

                          <div class="grid grid-cols-2 gap-2">
                            <div>
                              <label class="block text-xs text-gray-600 mb-1">Entity Type Name (for button)</label>
                              <input
                                v-model="component.settings.entity_type_name"
                                placeholder="e.g., Staff"
                                class="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                              />
                            </div>

                            <div class="flex items-center gap-2">
                              <label class="flex items-center text-xs text-gray-600">
                                <input
                                  v-model="component.settings.auto_height"
                                  type="checkbox"
                                  class="rounded border-gray-300 text-blue-600 focus:ring-blue-dark mr-1"
                                />
                                Auto Height
                              </label>
                            </div>
                          </div>

                          <div class="text-xs text-gray-500 italic mt-1">
                            Configure table columns in the "Table Columns" tab above
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Modal Footer -->
          <div class="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              @click="closeDisplayModal"
              class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              @click="saveDisplayConfig"
              class="btn-primary-sm"
            >
              <font-awesome-icon :icon="['fas', 'save']" />
              Save Configuration
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Entity Type Modal (existing - unchanged) -->
    <div
      v-if="showCreateModal"
      class="fixed inset-0 bg-gray-500/75 overflow-y-auto h-full w-full z-50"
      @click.self="showCreateModal = false"
    >
      <div class="relative top-20 mx-auto p-5 w-96">
        <div class="bg-white rounded-lg shadow-xl p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Create New Entity Type</h3>
          <input
            v-model="newEntityTypeName"
            placeholder="Entity type name (e.g., 'clinic', 'department')"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-dark"
            @keyup.enter="createEntityType"
          />
          <div class="mt-4 flex justify-end space-x-3">
            <button
              @click="showCreateModal = false"
              class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              @click="createEntityType"
              :disabled="!newEntityTypeName.trim()"
              class="btn-primary-sm"
            >
              Create
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faTimes, faPlus, faSave, faEdit, faTrash, faToggleOn, faToggleOff,
  faArrowUp, faArrowDown, faInbox, faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';

library.add(
  faTimes, faPlus, faSave, faEdit, faTrash, faToggleOn, faToggleOff,
  faArrowUp, faArrowDown, faInbox, faExclamationTriangle
);

export default {
  name: 'EntityTypeConfiguration',
  components: {
    FontAwesomeIcon,
    ComponentHeader
  },
  data() {
    return {
      entityTypes: [],
      entityAttributes: [],
      showAttributesModal: false,
      showDisplayModal: false,
      showCreateModal: false,
      showAddAttributeForm: false,
      editingEntityType: null,
      editingAttribute: null,
      newEntityTypeName: '',
      newAttribute: {
        name: '',
        display_name: '',
        description: '',
        field_type: 'text',
        is_required: false,
        display_group: '',
        display_order: 0
      },
      displayConfig: {
        layout_config: {
          tabs: {}
        },
        table_config: {
          columns: []
        }
      },
      attributeCounts: {},
      activeDisplayTab: 'table',
      editingColumnIndex: null
    };
  },
  computed: {
    availableAttributesForTable() {
      // Attributes that aren't already in the table
      const usedAttributeNames = (this.displayConfig.table_config?.columns || [])
        .filter(col => col.type === 'attribute')
        .map(col => col.attribute_name);

      return this.entityAttributes.filter(attr =>
        !usedAttributeNames.includes(attr.name)
      );
    },
    orderedTabNames() {
      const tabs = this.displayConfig.layout_config?.tabs || {};
      const tabOrder = this.displayConfig.layout_config?.tab_order || [];
      const allTabNames = Object.keys(tabs);
      // Return tabs in configured order, appending any unconfigured tabs at the end
      const ordered = tabOrder.filter(name => allTabNames.includes(name));
      const remaining = allTabNames.filter(name => !ordered.includes(name));
      return [...ordered, ...remaining];
    }
  },
  mounted() {
    this.fetchEntityTypes();
  },
  methods: {
    async fetchEntityTypes() {
      try {
        const response = await axios.get('/api/entity-types/');
        this.entityTypes = response.data;

        for (const entityType of this.entityTypes) {
          await this.fetchAttributeCount(entityType.id);
        }
      } catch (error) {
        console.error('Error fetching entity types:', error);
      }
    },

    async fetchAttributeCount(entityTypeId) {
      try {
        const response = await axios.get('/api/entity-attributes/', {
          params: { entity_type_id: entityTypeId }
        });
        this.attributeCounts[entityTypeId] = response.data.length;
      } catch (error) {
        console.error('Error fetching attribute count:', error);
        this.attributeCounts[entityTypeId] = 0;
      }
    },

    async fetchAttributes(entityTypeId) {
      try {
        const response = await axios.get('/api/entity-attributes/', {
          params: {
            entity_type_id: entityTypeId,
            show_inactive: true
          }
        });
        this.entityAttributes = response.data.sort((a, b) => a.display_order - b.display_order);
      } catch (error) {
        console.error('Error fetching attributes:', error);
        this.entityAttributes = [];
      }
    },

    getAttributeCount(entityTypeId) {
      return this.attributeCounts[entityTypeId] || 0;
    },

    getTableColumnCount(displayConfig) {
      return displayConfig?.table_config?.columns?.length || 0;
    },

    getComponentCount(displayConfig) {
      if (!displayConfig || !displayConfig.layout_config || !displayConfig.layout_config.tabs) return 0;

      let count = 0;
      Object.values(displayConfig.layout_config.tabs).forEach(tabComponents => {
        count += tabComponents.length;
      });
      return count;
    },

    hasTemplate(typeName) {
      return ['practice', 'location', 'staff'].includes(typeName.toLowerCase());
    },

    async manageAttributes(entityType) {
      this.editingEntityType = entityType;
      await this.fetchAttributes(entityType.id);
      this.showAttributesModal = true;
    },

    async configureDisplay(entityType) {
      this.editingEntityType = entityType;
      await this.fetchAttributes(entityType.id);

      this.displayConfig = JSON.parse(JSON.stringify(entityType.display_config || {
        layout_config: { tabs: {} },
        table_config: { columns: [] }
      }));

      // Ensure structure exists
      if (!this.displayConfig.layout_config) {
        this.displayConfig.layout_config = { tabs: {} };
      }
      if (!this.displayConfig.layout_config.tabs) {
        this.displayConfig.layout_config.tabs = {};
      }
      if (!this.displayConfig.table_config) {
        this.displayConfig.table_config = { columns: [] };
      }
      if (!this.displayConfig.table_config.columns) {
        this.displayConfig.table_config.columns = [];
      }

      // Add default Overview tab if no tabs exist
      if (Object.keys(this.displayConfig.layout_config.tabs).length === 0) {
        this.displayConfig.layout_config.tabs.Overview = [];
      }
      // Initialize tab_order from existing tab keys if not present
      if (!this.displayConfig.layout_config.tab_order) {
        this.displayConfig.layout_config.tab_order = Object.keys(this.displayConfig.layout_config.tabs);
      }

      this.activeDisplayTab = 'table';
      this.editingColumnIndex = null;
      this.showDisplayModal = true;
    },

    closeAttributesModal() {
      this.showAttributesModal = false;
      this.showAddAttributeForm = false;
      this.editingEntityType = null;
      this.editingAttribute = null;
      this.resetNewAttribute();
    },

    closeDisplayModal() {
      this.showDisplayModal = false;
      this.editingEntityType = null;
      this.editingColumnIndex = null;
    },

    resetNewAttribute() {
      this.newAttribute = {
        name: '',
        display_name: '',
        description: '',
        field_type: 'text',
        is_required: false,
        display_group: '',
        display_order: this.entityAttributes.length
      };
    },

    cancelAddAttribute() {
      this.showAddAttributeForm = false;
      this.editingAttribute = null;
      this.resetNewAttribute();
    },

    async saveAttribute() {
      if (!this.newAttribute.name || !this.newAttribute.display_name) {
        alert('Field name and display name are required');
        return;
      }

      try {
        const data = {
          ...this.newAttribute,
          entity_type: this.editingEntityType.id,
          display_order: this.editingAttribute ? this.newAttribute.display_order : this.entityAttributes.length
        };

        if (this.editingAttribute) {
          await axios.patch(`/api/entity-attributes/${this.editingAttribute.id}/`, data);
        } else {
          await axios.post('/api/entity-attributes/', data);
        }

        await this.fetchAttributes(this.editingEntityType.id);
        await this.fetchAttributeCount(this.editingEntityType.id);
        this.showAddAttributeForm = false;
        this.editingAttribute = null;
        this.resetNewAttribute();
      } catch (error) {
        console.error('Error saving attribute:', error);
        alert('Failed to save attribute');
      }
    },

    editAttribute(attribute) {
      this.editingAttribute = attribute;
      this.newAttribute = {
        name: attribute.name,
        display_name: attribute.display_name,
        description: attribute.description || '',
        field_type: attribute.field_type,
        is_required: attribute.is_required,
        display_group: attribute.display_group || '',
        display_order: attribute.display_order
      };
      this.showAddAttributeForm = true;
    },

    async deleteAttribute(attribute) {
      if (!confirm(`Delete attribute "${attribute.display_name}"?`)) return;

      try {
        await axios.delete(`/api/entity-attributes/${attribute.id}/`);
        await this.fetchAttributes(this.editingEntityType.id);
        await this.fetchAttributeCount(this.editingEntityType.id);
      } catch (error) {
        console.error('Error deleting attribute:', error);
        alert('Failed to delete attribute');
      }
    },

    async moveAttribute(index, direction) {
      const attributes = [...this.entityAttributes];
      const targetIndex = index + direction;

      if (targetIndex < 0 || targetIndex >= attributes.length) return;

      [attributes[index], attributes[targetIndex]] = [attributes[targetIndex], attributes[index]];

      const updates = attributes.map((attr, idx) => ({
        id: attr.id,
        display_order: idx
      }));

      try {
        await axios.post('/api/entity-attributes/reorder/', { updates });
        this.entityAttributes = attributes;
      } catch (error) {
        console.error('Error reordering attributes:', error);
      }
    },

    // TABLE COLUMN CONFIGURATION METHODS
    addColumnFromAttribute(attribute) {
      if (!this.displayConfig.table_config.columns) {
        this.displayConfig.table_config.columns = [];
      }

      this.displayConfig.table_config.columns.push({
        type: 'attribute',
        attribute_name: attribute.name,
        header_name: attribute.display_name,
        width: 150,
        sortable: true,
        filterable: true
      });
    },

    addSpecialColumn(columnType) {
      if (!this.displayConfig.table_config.columns) {
        this.displayConfig.table_config.columns = [];
      }

      if (columnType === 'identifier') {
        this.displayConfig.table_config.columns.push({
          type: 'identifier',
          identifier_type: 'NPI',
          header_name: 'NPI',
          width: 120,
          sortable: true,
          filterable: true
        });
      } else if (columnType === 'action') {
        this.displayConfig.table_config.columns.push({
          type: 'action',
          action_type: 'sync',
          header_name: 'Sync',
          width: 80,
          sortable: false,
          filterable: false
        });
      }
    },

    moveTableColumn(index, direction) {
      const columns = this.displayConfig.table_config.columns;
      const targetIndex = index + direction;

      if (targetIndex < 0 || targetIndex >= columns.length) return;

      [columns[index], columns[targetIndex]] = [columns[targetIndex], columns[index]];
    },

    editTableColumn(index) {
      this.editingColumnIndex = this.editingColumnIndex === index ? null : index;
    },

    removeTableColumn(index) {
      this.displayConfig.table_config.columns.splice(index, 1);
    },

    clearAllTableColumns() {
      if (confirm('Remove all table columns?')) {
        this.displayConfig.table_config.columns = [];
      }
    },

    getColumnDisplayName(column) {
      if (column.type === 'attribute') {
        const attr = this.entityAttributes.find(a => a.name === column.attribute_name);
        return attr?.display_name || column.attribute_name;
      } else if (column.type === 'identifier') {
        return column.identifier_type;
      } else if (column.type === 'action') {
        return column.action_type;
      }
      return 'Unknown';
    },

    getColumnTypeLabel(column) {
      if (column.type === 'attribute') {
        return `Attribute: ${column.attribute_name}`;
      } else if (column.type === 'identifier') {
        return `Identifier: ${column.identifier_type}`;
      } else if (column.type === 'action') {
        return `Action: ${column.action_type}`;
      }
      return column.type;
    },

    // PAGE LAYOUT METHODS (existing)
    addTab() {
      const tabName = prompt('Enter tab name:');
      if (tabName && !this.displayConfig.layout_config.tabs[tabName]) {
        this.displayConfig.layout_config.tabs[tabName] = [];
        if (!this.displayConfig.layout_config.tab_order) {
          this.displayConfig.layout_config.tab_order = Object.keys(this.displayConfig.layout_config.tabs).filter(n => n !== tabName);
        }
        this.displayConfig.layout_config.tab_order.push(tabName);
      }
    },

    renameTab(oldName, newName) {
      if (newName && newName !== oldName && !this.displayConfig.layout_config.tabs[newName]) {
        const components = this.displayConfig.layout_config.tabs[oldName];
        delete this.displayConfig.layout_config.tabs[oldName];
        this.displayConfig.layout_config.tabs[newName] = components;
        // Update tab_order
        const order = this.displayConfig.layout_config.tab_order;
        if (order) {
          const idx = order.indexOf(oldName);
          if (idx !== -1) order[idx] = newName;
        }
      }
    },

    removeTab(tabName) {
      if (confirm(`Remove tab "${tabName}"?`)) {
        delete this.displayConfig.layout_config.tabs[tabName];
        // Remove from tab_order
        const order = this.displayConfig.layout_config.tab_order;
        if (order) {
          const idx = order.indexOf(tabName);
          if (idx !== -1) order.splice(idx, 1);
        }
      }
    },

    moveTab(index, direction) {
      if (!this.displayConfig.layout_config.tab_order) {
        this.displayConfig.layout_config.tab_order = [...this.orderedTabNames];
      }
      const order = this.displayConfig.layout_config.tab_order;
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= order.length) return;
      [order[index], order[targetIndex]] = [order[targetIndex], order[index]];
    },

    addComponent(tabName) {
      if (!this.displayConfig.layout_config.tabs[tabName]) {
        this.displayConfig.layout_config.tabs[tabName] = [];
      }
      this.displayConfig.layout_config.tabs[tabName].push({
        component: 'basic_info',
        order: this.displayConfig.layout_config.tabs[tabName].length,
        settings: {}
      });
    },

    onComponentTypeChange(tabName, index, component) {
      // Initialize settings object if it doesn't exist
      if (!component.settings) {
        component.settings = {};
      }

      // Initialize entity_detail_table settings
      if (component.component === 'entity_detail_table') {
        if (!component.settings.child_entity_type_id) {
          component.settings.child_entity_type_id = null;
        }
        if (!component.settings.entity_type_name) {
          component.settings.entity_type_name = '';
        }
        if (!component.settings.title) {
          component.settings.title = '';
        }
        if (component.settings.auto_height === undefined) {
          component.settings.auto_height = false;
        }
      }

    },

    removeComponent(tabName, index) {
      this.displayConfig.layout_config.tabs[tabName].splice(index, 1);
      // Re-sync order values after removal
      this.displayConfig.layout_config.tabs[tabName].forEach((comp, i) => {
        comp.order = i;
      });
    },

    moveComponent(tabName, index, direction) {
      const components = this.displayConfig.layout_config.tabs[tabName];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= components.length) return;
      [components[index], components[targetIndex]] = [components[targetIndex], components[index]];
      // Re-sync order values to match array position
      components.forEach((comp, i) => {
        comp.order = i;
      });
    },

    async saveDisplayConfig() {
      try {
        await axios.patch(`/api/entity-types/${this.editingEntityType.id}/`, {
          display_config: this.displayConfig
        });

        await this.fetchEntityTypes();
        this.closeDisplayModal();
        alert('Display configuration saved successfully');
      } catch (error) {
        console.error('Error saving display configuration:', error);
        alert('Failed to save display configuration');
      }
    },

    async createEntityType() {
      if (!this.newEntityTypeName.trim()) return;

      try {
        await axios.post('/api/entity-types/', {
          type_name: this.newEntityTypeName,
          display_config: {
            layout_config: {
              tabs: {
                'Overview': [
                  { component: 'basic_info', order: 0 }
                ]
              }
            },
            table_config: {
              columns: []
            }
          },
          is_active: true
        });

        this.newEntityTypeName = '';
        this.showCreateModal = false;
        await this.fetchEntityTypes();
      } catch (error) {
        console.error('Error creating entity type:', error);
        alert('Failed to create entity type');
      }
    },

    async initializeFromTemplate(entityType) {
      try {
        await axios.post('/api/entity-types/initialize-from-template/', {
          type_name: entityType.type_name
        });

        await axios.post('/api/entity-attributes/initialize_from_schema/', {
          entity_type_id: entityType.id
        });

        await this.fetchEntityTypes();
        await this.fetchAttributeCount(entityType.id);
        alert(`Initialized ${entityType.type_name} from template`);
      } catch (error) {
        console.error('Error initializing from template:', error);
        alert('Failed to initialize from template');
      }
    }
  }
};
</script>

<style scoped>
/* Component-specific styles if needed */
</style>
